/**
 * Group Service
 * Handles collaborative travel planning with group management, invitations, and real-time sync
 */

import { supabase } from '../lib/supabase';

/**
 * Create a new trip group
 */
export async function createTripGroup(tripId, groupData, userId) {
  try {
    const { data, error } = await supabase?.from('trip_groups')?.insert({
        trip_id: tripId,
        name: groupData?.name,
        description: groupData?.description,
        created_by: userId
      })?.select()?.single();

    if (error) throw error;

    // Add creator as owner
    await addGroupMember(data?.id, userId, 'owner');

    return { success: true, group: convertGroupFromDb(data) };
  } catch (error) {
    console.error('Error creating trip group:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get all groups for a trip
 */
export async function getTripGroups(tripId) {
  try {
    const { data, error } = await supabase?.from('trip_groups')?.select(`
        *,
        group_members(count)
      `)?.eq('trip_id', tripId)?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(convertGroupFromDb) || [];
  } catch (error) {
    console.error('Error fetching trip groups:', error);
    return [];
  }
}

/**
 * Get group details with members
 */
export async function getGroupDetails(groupId) {
  try {
    const { data, error } = await supabase?.from('trip_groups')?.select(`
        *,
        group_members(
          id,
          user_id,
          role,
          joined_at
        )
      `)?.eq('id', groupId)?.single();

    if (error) throw error;

    return convertGroupFromDb(data);
  } catch (error) {
    console.error('Error fetching group details:', error);
    return null;
  }
}

/**
 * Add member to group
 */
export async function addGroupMember(groupId, userId, role = 'viewer') {
  try {
    const { data, error } = await supabase?.from('group_members')?.insert({
        group_id: groupId,
        user_id: userId,
        role: role
      })?.select()?.single();

    if (error) throw error;

    return { success: true, member: convertMemberFromDb(data) };
  } catch (error) {
    console.error('Error adding group member:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Send group invitation via email
 */
export async function sendGroupInvitation(groupId, inviterUserId, inviteeEmail, role = 'viewer') {
  try {
    // Generate invitation token
    const invitationToken = generateInvitationToken();
    const expiresAt = new Date();
    expiresAt?.setDate(expiresAt?.getDate() + 7); // Expires in 7 days

    // Check if invitee is already a user
    const { data: existingUser } = await supabase?.from('auth.users')?.select('id')?.eq('email', inviteeEmail)?.maybeSingle();

    const { data, error } = await supabase?.from('group_invitations')?.insert({
        group_id: groupId,
        inviter_id: inviterUserId,
        invitee_email: inviteeEmail,
        invitee_user_id: existingUser?.id || null,
        role: role,
        invitation_token: invitationToken,
        expires_at: expiresAt?.toISOString()
      })?.select()?.single();

    if (error) throw error;

    // Send email via edge function
    await sendInvitationEmail({
      inviteeEmail,
      invitationToken,
      groupId
    });

    return { success: true, invitation: convertInvitationFromDb(data) };
  } catch (error) {
    console.error('Error sending group invitation:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get pending invitations for current user
 */
export async function getPendingInvitations(userId) {
  try {
    const { data: userData } = await supabase?.auth?.getUser();
    const userEmail = userData?.user?.email;

    const { data, error } = await supabase?.from('group_invitations')?.select(`
        *,
        trip_groups(
          name,
          description
        )
      `)?.or(`invitee_user_id.eq.${userId},invitee_email.eq.${userEmail}`)?.eq('status', 'pending')?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(convertInvitationFromDb) || [];
  } catch (error) {
    console.error('Error fetching pending invitations:', error);
    return [];
  }
}

/**
 * Accept group invitation
 */
export async function acceptInvitation(invitationId, userId) {
  try {
    // Get invitation details
    const { data: invitation, error: invError } = await supabase?.from('group_invitations')?.select('*')?.eq('id', invitationId)?.single();

    if (invError) throw invError;

    // Update invitation status
    const { error: updateError } = await supabase?.from('group_invitations')?.update({ 
        status: 'accepted',
        invitee_user_id: userId
      })?.eq('id', invitationId);

    if (updateError) throw updateError;

    // Add user to group
    await addGroupMember(invitation?.group_id, userId, invitation?.role);

    return { success: true };
  } catch (error) {
    console.error('Error accepting invitation:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Decline group invitation
 */
export async function declineInvitation(invitationId) {
  try {
    const { error } = await supabase?.from('group_invitations')?.update({ status: 'declined' })?.eq('id', invitationId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error declining invitation:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Add group expense
 */
export async function addGroupExpense(groupId, expenseData, userId) {
  try {
    const { data, error } = await supabase?.from('group_expenses')?.insert({
        group_id: groupId,
        paid_by: userId,
        category: expenseData?.category,
        description: expenseData?.description,
        amount: expenseData?.amount,
        expense_date: expenseData?.expenseDate,
        receipt_url: expenseData?.receiptUrl
      })?.select()?.single();

    if (error) throw error;

    // Create expense splits
    if (expenseData?.splits && expenseData?.splits?.length > 0) {
      await createExpenseSplits(data?.id, expenseData?.splits);
    }

    return { success: true, expense: convertExpenseFromDb(data) };
  } catch (error) {
    console.error('Error adding group expense:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Create expense splits
 */
export async function createExpenseSplits(expenseId, splits) {
  try {
    const splitData = splits?.map(split => ({
      expense_id: expenseId,
      user_id: split?.userId,
      split_amount: split?.amount,
      is_paid: false
    }));

    const { error } = await supabase?.from('expense_splits')?.insert(splitData);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error creating expense splits:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get group expenses with splits
 */
export async function getGroupExpenses(groupId) {
  try {
    const { data, error } = await supabase?.from('group_expenses')?.select(`
        *,
        expense_splits(
          id,
          user_id,
          split_amount,
          is_paid,
          paid_at
        )
      `)?.eq('group_id', groupId)?.order('expense_date', { ascending: false });

    if (error) throw error;

    return data?.map(convertExpenseFromDb) || [];
  } catch (error) {
    console.error('Error fetching group expenses:', error);
    return [];
  }
}

/**
 * Mark expense split as paid
 */
export async function markSplitAsPaid(splitId) {
  try {
    const { error } = await supabase?.from('expense_splits')?.update({ 
        is_paid: true,
        paid_at: new Date()?.toISOString()
      })?.eq('id', splitId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error marking split as paid:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get user's expense summary for a group
 */
export async function getUserExpenseSummary(groupId, userId) {
  try {
    const { data, error } = await supabase?.from('expense_splits')?.select(`
        split_amount,
        is_paid,
        group_expenses!inner(
          group_id,
          paid_by
        )
      `)?.eq('user_id', userId)?.eq('group_expenses.group_id', groupId);

    if (error) throw error;

    const totalOwed = data?.reduce((sum, split) => 
      !split?.is_paid ? sum + parseFloat(split?.split_amount) : sum, 0
    ) || 0;

    const totalPaid = data?.reduce((sum, split) => 
      split?.is_paid ? sum + parseFloat(split?.split_amount) : sum, 0
    ) || 0;

    return {
      totalOwed,
      totalPaid,
      totalExpenses: totalOwed + totalPaid
    };
  } catch (error) {
    console.error('Error fetching expense summary:', error);
    return { totalOwed: 0, totalPaid: 0, totalExpenses: 0 };
  }
}

/**
 * Subscribe to group changes (real-time)
 */
export function subscribeToGroupChanges(groupId, onUpdate) {
  const channel = supabase?.channel(`group_${groupId}`)?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'group_itinerary_items',
        filter: `group_id=eq.${groupId}`
      },
      (payload) => {
        onUpdate({ type: 'itinerary', payload });
      }
    )?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'group_expenses',
        filter: `group_id=eq.${groupId}`
      },
      (payload) => {
        onUpdate({ type: 'expense', payload });
      }
    )?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'group_members',
        filter: `group_id=eq.${groupId}`
      },
      (payload) => {
        onUpdate({ type: 'member', payload });
      }
    )?.subscribe();

  return channel;
}

/**
 * Unsubscribe from group changes
 */
export function unsubscribeFromGroupChanges(channel) {
  if (channel) {
    supabase?.removeChannel(channel);
  }
}

/**
 * Send invitation email via edge function
 */
async function sendInvitationEmail(invitationData) {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-group-invitation', {
      body: invitationData
    });

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error sending invitation email:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Generate random invitation token
 */
function generateInvitationToken() {
  return Math.random()?.toString(36)?.substring(2, 15) + 
         Math.random()?.toString(36)?.substring(2, 15);
}

/**
 * Convert group from database format (snake_case) to app format (camelCase)
 */
function convertGroupFromDb(group) {
  if (!group) return null;

  return {
    id: group?.id,
    tripId: group?.trip_id,
    name: group?.name,
    description: group?.description,
    createdBy: group?.created_by,
    memberCount: group?.group_members?.[0]?.count || 0,
    members: group?.group_members?.map(convertMemberFromDb) || [],
    createdAt: group?.created_at,
    updatedAt: group?.updated_at
  };
}

/**
 * Convert member from database format
 */
function convertMemberFromDb(member) {
  if (!member) return null;

  return {
    id: member?.id,
    groupId: member?.group_id,
    userId: member?.user_id,
    role: member?.role,
    joinedAt: member?.joined_at
  };
}

/**
 * Convert invitation from database format
 */
function convertInvitationFromDb(invitation) {
  if (!invitation) return null;

  return {
    id: invitation?.id,
    groupId: invitation?.group_id,
    inviterId: invitation?.inviter_id,
    inviteeEmail: invitation?.invitee_email,
    inviteeUserId: invitation?.invitee_user_id,
    role: invitation?.role,
    status: invitation?.status,
    invitationToken: invitation?.invitation_token,
    expiresAt: invitation?.expires_at,
    groupName: invitation?.trip_groups?.name,
    groupDescription: invitation?.trip_groups?.description,
    createdAt: invitation?.created_at,
    updatedAt: invitation?.updated_at
  };
}

/**
 * Convert expense from database format
 */
function convertExpenseFromDb(expense) {
  if (!expense) return null;

  return {
    id: expense?.id,
    groupId: expense?.group_id,
    paidBy: expense?.paid_by,
    category: expense?.category,
    description: expense?.description,
    amount: parseFloat(expense?.amount),
    expenseDate: expense?.expense_date,
    receiptUrl: expense?.receipt_url,
    splits: expense?.expense_splits?.map(split => ({
      id: split?.id,
      userId: split?.user_id,
      splitAmount: parseFloat(split?.split_amount),
      isPaid: split?.is_paid,
      paidAt: split?.paid_at
    })) || [],
    createdAt: expense?.created_at,
    updatedAt: expense?.updated_at
  };
}